import { LogOut, Settings } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// =============================================================================
// Jedno miejsce zalogowanego użytkownika — prawy górny róg.
//
// Właściciel (13.09.2026): „obecnie są dwa miejsca, gdzie jest widoczny
// zalogowany użytkownik, a wszystko powinno być w prawym górnym rogu jako
// jedno miejsce, wylogowanie również". Do tej pory sidebar miał stopkę
// (inicjał, nazwisko, rola, ikona wylogowania), a topbar — drugi awatar bez
// żadnej akcji. Stopki nie ma; awatar w topbarze otwiera menu z tożsamością,
// ustawieniami i wylogowaniem. Ten sam komponent w Admin/Partner/SuperAdmin,
// żeby użytkownik uczył się jednego miejsca raz.
// Bramka: tests/unit/apps/jednoMiejsceUzytkownika.test.tsx
// =============================================================================

export interface UserMenuProps {
  imie?: string | null;
  email?: string | null;
  /** Etykieta roli już przetłumaczona („Właściciel”, „Partner”, „Super Admin”). */
  rola: string;
  /** Trasa „Ustawienia” właściwa dla danego layoutu; bez niej pozycji nie ma. */
  ustawieniaHref?: string;
  onSignOut: () => void | Promise<void>;
  /** Litera zapasowa, gdy nie ma ani nazwiska, ani e-maila. */
  inicjalZapasowy?: string;
}

export function inicjalUzytkownika(
  imie?: string | null,
  email?: string | null,
  zapasowy = 'U'
): string {
  return (imie?.trim()?.[0] || email?.trim()?.[0] || zapasowy).toUpperCase();
}

export function UserMenu({
  imie,
  email,
  rola,
  ustawieniaHref,
  onSignOut,
  inicjalZapasowy = 'U',
}: UserMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [otwarte, setOtwarte] = useState(false);
  const korzenRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const nazwa = imie?.trim() || email || '';

  // Klik poza menu i Escape zamykają — jak każde menu, którego użytkownik
  // nie chce szukać drugi raz.
  useEffect(() => {
    if (!otwarte) return;
    const naKlik = (e: MouseEvent) => {
      if (korzenRef.current && !korzenRef.current.contains(e.target as Node)) setOtwarte(false);
    };
    const naKlawisz = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOtwarte(false);
    };
    document.addEventListener('mousedown', naKlik);
    document.addEventListener('keydown', naKlawisz);
    return () => {
      document.removeEventListener('mousedown', naKlik);
      document.removeEventListener('keydown', naKlawisz);
    };
  }, [otwarte]);

  return (
    <div ref={korzenRef} className="relative" data-testid="menu-uzytkownika">
      <button
        type="button"
        className="avatar cursor-pointer border-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora-brand"
        aria-haspopup="menu"
        aria-expanded={otwarte}
        aria-controls={menuId}
        aria-label={`${t('auth.account', 'Konto')}: ${nazwa}`}
        title={nazwa}
        onClick={() => setOtwarte((o) => !o)}
      >
        {inicjalUzytkownika(imie, email, inicjalZapasowy)}
      </button>

      {otwarte && (
        <div
          id={menuId}
          role="menu"
          aria-label={t('auth.account', 'Konto')}
          className="absolute right-0 top-full mt-2 w-64 bg-aurora-raised border border-aurora-border-strong rounded-ids-md shadow-elev-3 py-1 animate-in fade-in-0 zoom-in-95"
          style={{ zIndex: 'var(--ids-z-dropdown)' }}
        >
          <div className="flex items-center gap-3 px-3 py-2.5 border-b border-aurora-border">
            <span className="avatar flex-shrink-0">
              {inicjalUzytkownika(imie, email, inicjalZapasowy)}
            </span>
            <div className="min-w-0 leading-tight">
              <p className="text-[13px] font-semibold text-aurora-text truncate">{nazwa}</p>
              <p className="text-[11px] text-aurora-subtle truncate">{rola}</p>
              {email && imie && (
                <p className="text-[11px] text-aurora-subtle truncate" title={email}>
                  {email}
                </p>
              )}
            </div>
          </div>

          {ustawieniaHref && (
            <button
              type="button"
              role="menuitem"
              className="navitem"
              onClick={() => {
                setOtwarte(false);
                navigate(ustawieniaHref);
              }}
            >
              <Settings className="w-4 h-4" />
              {t('nav.settings', 'Ustawienia')}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="navitem hover:text-aurora-danger"
            onClick={() => {
              setOtwarte(false);
              void onSignOut();
            }}
          >
            <LogOut className="w-4 h-4" />
            {t('auth.signOut', 'Wyloguj')}
          </button>
        </div>
      )}
    </div>
  );
}
